import React, { useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

const ProjectCarousel = ({ images, projectName }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);

  if (!images || images.length === 0) {
    return null;
  }

  const nextImage = () => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    setCurrentIndex((prevIndex) =>
      prevIndex === images.length - 1 ? 0 : prevIndex + 1
    );
    setTimeout(() => setIsTransitioning(false), 500);
  };

  const previousImage = () => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    setCurrentIndex((prevIndex) =>
      prevIndex === 0 ? images.length - 1 : prevIndex - 1
    );
    setTimeout(() => setIsTransitioning(false), 500);
  };

  const goToImage = (index) => {
    if (isTransitioning || index === currentIndex) return;
    setIsTransitioning(true);
    setCurrentIndex(index);
    setTimeout(() => setIsTransitioning(false), 500);
  };

  return (
    <div className="relative w-full h-48 md:h-56 group">
      {/* Carousel wrapper */}
      <div className="relative h-full overflow-hidden rounded-t-2xl bg-cute-dark/50">
        {images.map((image, index) => (
          <div
            key={index}
            className={`absolute w-full h-full transition-all duration-500 ease-out ${
              currentIndex === index
                ? 'opacity-100 scale-100 z-20'
                : 'opacity-0 scale-95 z-10'
            }`}
          >
            <img
              src={image}
              alt={`${projectName} screenshot ${index + 1}`}
              className="w-full h-full object-cover"
              onLoad={() => setImageLoaded(true)}
            />
            {/* Overlay gradient */}
            <div className="absolute inset-0 bg-gradient-to-t from-cute-dark/30 to-transparent" />
          </div>
        ))}
      </div>

      {/* Image counter badge */}
      <div className="absolute top-3 right-3 z-30 px-3 py-1 rounded-full bg-cute-dark/70 backdrop-blur-sm border border-white/10 text-white/80 text-xs font-medium">
        {currentIndex + 1} / {images.length}
      </div>

      {/* Slider indicators */}
      {images.length > 1 && (
        <div className="absolute z-30 flex gap-2 -translate-x-1/2 bottom-4 left-1/2">
          {images.map((_, index) => (
            <button
              key={index}
              type="button"
              onClick={() => goToImage(index)}
              disabled={isTransitioning}
              className={`transition-all duration-300 rounded-full ${
                currentIndex === index
                  ? 'w-6 h-2 bg-kawaii-pink'
                  : 'w-2 h-2 bg-white/40 hover:bg-white/60'
              }`}
              aria-current={currentIndex === index}
              aria-label={`Slide ${index + 1}`}
            />
          ))}
        </div>
      )}

      {/* Navigation Buttons */}
      {images.length > 1 && (
        <>
          <button
            type="button"
            onClick={previousImage}
            disabled={isTransitioning}
            className="absolute top-1/2 left-2 z-30 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
          >
            <span className="flex items-center justify-center w-8 h-8 rounded-full bg-cute-dark/80 backdrop-blur-sm border border-white/10 hover:border-kawaii-pink/30 hover:bg-cute-dark transition-all duration-300">
              <ChevronLeft className="w-4 h-4 text-white" />
            </span>
          </button>

          <button
            type="button"
            onClick={nextImage}
            disabled={isTransitioning}
            className="absolute top-1/2 right-2 z-30 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
          >
            <span className="flex items-center justify-center w-8 h-8 rounded-full bg-cute-dark/80 backdrop-blur-sm border border-white/10 hover:border-kawaii-pink/30 hover:bg-cute-dark transition-all duration-300">
              <ChevronRight className="w-4 h-4 text-white" />
            </span>
          </button>
        </>
      )}

      {/* Loading state */}
      {!imageLoaded && (
        <div className="absolute inset-0 flex items-center justify-center bg-cute-card z-50 rounded-t-2xl">
          <div className="relative">
            <div className="w-10 h-10 border-3 border-kawaii-purple/30 rounded-full" />
            <div className="absolute top-0 left-0 w-10 h-10 border-3 border-kawaii-pink border-t-transparent rounded-full animate-spin" />
          </div>
        </div>
      )}
    </div>
  );
};

export default ProjectCarousel;
